/**
 * DomesticHeatPumpCalculatorPage — 1:1 port of
 * `docs/calc-references/domestic-heat-pump.html`.
 *
 * Task 3.4 of the May 2026 calculator exact-match replacement sprint
 * (`docs/superpowers/plans/2026-05-15-calculator-exact-match-replacement.md`).
 *
 * Replaces the previous CalcWizard-based 3-step shell with a single-page
 * 10-step ladder that mirrors the customer-facing reference HTML:
 *
 *   1. Property type           (chip picker)
 *   2. Covered area            (number + sqft/sqm toggle + conversion hint)
 *   3. Number of washrooms     (chip picker — bands)
 *   4. Family members          (chip picker — exact + bands)
 *   5. Staff on heat pump      (chip picker — bands)
 *   6. Reverse line + pump?    (yes/no chips + explanation paragraph)
 *   7. Climate zone            (chip picker — 5 zones, temp ranges in label)
 *   8. Daily hot-water usage   (chip picker — low/medium/high w/ verbose labels)
 *   9. Jacuzzi / bath tubs     (chip picker — 0..4)
 *  10. Kitchen connected?      (yes/no chips)
 *
 * Results panel below (renders on Calculate):
 *   - HW breakdown bars (Family / Staff / Kitchen / Jacuzzi, horizontal bars)
 *   - KPI strip (daily req · base per person · effective occupancy · area adder)
 *   - Recommended model card (Best pick — kw, tank, badges, features, notes)
 *   - Series card (HP1→HP2 flow diagram + totals) when needsSeries
 *   - M2 alternative card when eligible AND best !== M2
 *   - M6 budget card when eligible
 *   - Footnote with hot-water estimation methodology
 *   - Lead capture form + Send enquiry + Print + Convert to Proposal CTAs
 *
 * Band → numeric mapping (matches reference's `data-v` attrs):
 *   washrooms: 1-2→2, 3-4→3, 5-6→5, 7-8→7, 10+→10
 *   members:   1-2→2, 3, 4, 5, 6, 7, 8-10→9, 10+→12
 *   staff:     none→0, 1-2→1, 3-4→3, 5-6→5, 6+→7
 *   jacuzzi:   0..4 (last band "4+" → 4)
 *
 * The meeting-session gate is preserved. API wiring uses `domesticHpApi.calculate`
 * /`saveQuotation` from `@/api/calculators` which expects the
 * `LegacyDomesticHpInput` shape (see `backend/src/modules/calculators/
 * domestic-heat-pump/service.ts`).
 *
 * NOTE: The reference HTML POSTs lead capture to `/api/v1/leads` (storefront
 * widget path). In-AURA Meeting Mode does NOT have a public `/domestic-heat-pump
 * /lead` endpoint, so we route the lead through `saveQuotation` (the rep is
 * already on a Lead row) and display a transient success message. The lead
 * form fields are preserved for visual parity and to flow extra context into
 * the saved quotation's `inputs` payload.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Printer, Send } from 'lucide-react';
import { domesticHpApi, proposalsApi } from '@/api/calculators';
import type { DomesticHpInput, DomesticHpResult } from '@/api/calculators/types';
import { ChipPicker, type ChipOption } from '@/components/calc/ChipPicker';
import { KpiStrip } from '@/components/calc/KpiStrip';
import { readStoredSession, writeStoredSession } from './MeetingModeLauncher';
import { ConvertToProposalButton } from './_shared/ConvertToProposalButton';
import { PickAModelPanel } from './_shared/PickAModelPanel';

// ─── Reference catalog (verbatim from domestic-heat-pump.html MODELS array) ──

interface DhpModel {
  id: 'M1' | 'M2' | 'M3' | 'M4' | 'M5' | 'M6';
  kw: number;
  tank: number;
  maxL: number;
  name: string;
  series: string;
  type: 'hotcold' | 'heatonly';
  maxSqft: number;
  maxMembers: number;
  maxWashrooms: number;
  kitchenOK: boolean | 'conditional';
  reverseLineOK: boolean;
  jacuzziOK: boolean | 'conditional';
  maxStaff: number;
  highUsageOK: boolean;
  notes: string;
  features: string[];
}

const MODELS: ReadonlyArray<DhpModel> = [
  { id:'M1', kw:5,  tank:200, maxL:200, name:'5 kW Hot & Cold — 200 L Tank',  series:'Compact',      type:'hotcold',  maxSqft:3000, maxMembers:3, maxWashrooms:2, kitchenOK:false,         reverseLineOK:true,  jacuzziOK:false,         maxStaff:0, highUsageOK:false, notes:'For very small families only. Heats in cold, cools in hot weather. Works to -5°C ambient. No kitchen or jacuzzi.', features:['Hot & cold output','200 L tank','Works to -5°C ambient','Circulation loop OK','Compact footprint'] },
  { id:'M2', kw:5,  tank:300, maxL:300, name:'5 kW Hot & Cold — 300 L Tank',  series:'Standard',     type:'hotcold',  maxSqft:5000, maxMembers:4, maxWashrooms:4, kitchenOK:false,         reverseLineOK:false, jacuzziOK:false,         maxStaff:0, highUsageOK:false, notes:'Good for flats and builder floors up to 5000 sq ft where kitchen and reverse line are not required. No jacuzzi.', features:['Hot & cold output','300 L tank','Up to 5000 sq ft','Max 4 members / 4 washrooms','No kitchen / no reverse line'] },
  { id:'M3', kw:7,  tank:300, maxL:300, name:'7 kW Hot & Cold — 300 L Tank',  series:'Standard',     type:'hotcold',  maxSqft:6000, maxMembers:5, maxWashrooms:5, kitchenOK:'conditional', reverseLineOK:true,  jacuzziOK:false,         maxStaff:0, highUsageOK:false, notes:'Kitchen OK only if family members ≤ 4. Reverse line supported. No jacuzzi.', features:['Hot & cold output','300 L tank','Up to 6000 sq ft','Reverse line OK','Kitchen OK (≤ 4 members)','No jacuzzi'] },
  { id:'M4', kw:7,  tank:500, maxL:500, name:'7 kW Hot & Cold — 500 L Tank',  series:'Premium',      type:'hotcold',  maxSqft:7500, maxMembers:6, maxWashrooms:6, kitchenOK:true,          reverseLineOK:true,  jacuzziOK:'conditional', maxStaff:2, highUsageOK:false, notes:'Reverse line supported. Kitchen OK. Staff up to 2. With bath tub, keep family size lower. Low–medium usage.', features:['Hot & cold output','500 L tank','Up to 7500 sq ft','Reverse line OK','Kitchen OK','Staff up to 2'] },
  { id:'M5', kw:11, tank:500, maxL:500, name:'11 kW Hot & Cold — 500 L Tank', series:'Premium Plus', type:'hotcold',  maxSqft:9000, maxMembers:7, maxWashrooms:7, kitchenOK:true,          reverseLineOK:true,  jacuzziOK:'conditional', maxStaff:2, highUsageOK:false, notes:'Reverse line supported. Kitchen OK. Staff up to 2. With bath tub, members should be 4–5. Low–medium usage.', features:['Hot & cold output','500 L tank','Up to 9000 sq ft','Reverse line OK','Kitchen OK','Staff up to 2'] },
  { id:'M6', kw:5,  tank:300, maxL:300, name:'5 kW Heating Only — 300 L Tank',series:'Budget',       type:'heatonly', maxSqft:4000, maxMembers:5, maxWashrooms:4, kitchenOK:true,          reverseLineOK:false, jacuzziOK:false,         maxStaff:0, highUsageOK:false, notes:'Budget model — heating only, no cooling output. No staff. No jacuzzi. If reverse line installed, limit family to 4 members.', features:['Heating only — no cooling','300 L tank','Up to 4000 sq ft','Kitchen OK','No staff / no jacuzzi','Budget-friendly'] },
];

interface SeriesConfig {
  label: string;
  m1: DhpModel;
  m2: DhpModel;
  totalKW: number;
  totalTank: number;
  when: string;
}

const SERIES: Record<'S1' | 'S2' | 'S3', SeriesConfig> = {
  S1: { label:'7 kW 300 L + 5 kW 300 L in Series', m1:MODELS[2], m2:MODELS[1], totalKW:12, totalTank:600,  when:'Effective occupancy up to 6 — with jacuzzi, reverse line and kitchen.' },
  S2: { label:'7 kW 500 L + 5 kW 300 L in Series', m1:MODELS[3], m2:MODELS[1], totalKW:12, totalTank:800,  when:'Effective occupancy above 6 — larger family or family with staff.' },
  S3: { label:'11 kW 500 L + 7 kW 500 L in Series',m1:MODELS[4], m2:MODELS[3], totalKW:18, totalTank:1000, when:'Bungalow above 11,000 sq ft, or effective occupancy ≥ 8 with jacuzzi, kitchen and pantry.' },
};

// ─── Hot-water estimation (verbatim from reference) ─────────────────────────

const BASE: Record<'north_hills' | 'warm_hot', { low: number; medium: number; high: number }> = {
  north_hills: { low: 35, medium: 55, high: 90 },
  warm_hot:    { low: 25, medium: 45, high: 65 },
};

type ClimateZone = 'extreme_cold' | 'hills' | 'north' | 'warm' | 'hot_dry';
type Usage = 'low' | 'medium' | 'high';
type YN = 'yes' | 'no';

function climGroup(c: ClimateZone): 'north_hills' | 'warm_hot' {
  return c === 'warm' || c === 'hot_dry' ? 'warm_hot' : 'north_hills';
}

function areaAdderPct(sqft: number, rev: YN): number {
  const r = rev === 'yes';
  if (sqft <= 3000) return 0;
  if (sqft <= 5000) return r ? 10 : 5;
  if (sqft <= 9000) return r ? 15 : 10;
  if (sqft <= 14000) return r ? 20 : 15;
  return r ? 25 : 20;
}

interface HwBreakdown {
  cg: 'north_hills' | 'warm_hot';
  base: number;
  pct: number;
  familyL: number;
  staffL: number;
  kitchenL: number;
  jacuzziL: number;
  total: number;
}

function estimateHW(
  climate: ClimateZone,
  usage: Usage,
  members: number,
  staff: number,
  rev: YN,
  sqft: number,
  kitchen: YN,
  jacuzzi: number,
): HwBreakdown {
  const cg = climGroup(climate);
  const base = BASE[cg][usage];
  const pct = areaAdderPct(sqft, rev);
  const familyL = Math.round(members * base * (1 + pct / 100));
  const staffL = staff * 30;
  const kitchenL = kitchen === 'yes' ? 100 : 0;
  const jacuzziL = jacuzzi * 150;
  const total = Math.ceil(familyL + staffL + kitchenL + jacuzziL);
  return { cg, base, pct, familyL, staffL, kitchenL, jacuzziL, total };
}

function checkModel(
  m: DhpModel,
  sqft: number,
  members: number,
  washrooms: number,
  kitchen: YN,
  rev: YN,
  jacuzzi: number,
  staff: number,
  usage: Usage,
  totalL: number,
): string[] {
  const f: string[] = [];
  if (sqft > m.maxSqft) f.push(`area exceeds ${m.maxSqft.toLocaleString()} sq ft`);
  if (members > m.maxMembers) f.push(`members (${members}) exceed ${m.maxMembers}`);
  if (washrooms > m.maxWashrooms) f.push('washrooms exceed ' + m.maxWashrooms);
  if (staff > m.maxStaff) f.push('staff not supported');
  if (rev === 'yes' && !m.reverseLineOK) f.push('reverse line not supported');
  if (kitchen === 'yes' && m.kitchenOK === false) f.push('kitchen not supported');
  if (kitchen === 'yes' && m.kitchenOK === 'conditional' && members > 4)
    f.push('kitchen OK only for ≤ 4 members');
  if (jacuzzi > 0 && m.jacuzziOK === false) f.push('jacuzzi not supported');
  if (jacuzzi > 0 && m.jacuzziOK === 'conditional' && members > 5)
    f.push('jacuzzi — reduce family size');
  if (usage === 'high' && !m.highUsageOK) f.push('high usage exceeds capacity');
  if (totalL > m.maxL) f.push(`daily demand (${totalL} L) exceeds tank (${m.maxL} L)`);
  return f;
}

function pickSeriesKey(
  sqft: number,
  members: number,
  staff: number,
  jacuzzi: number,
): 'S1' | 'S2' | 'S3' {
  const eff = members + staff * 0.5;
  if (sqft > 11000 || (eff >= 8 && jacuzzi >= 1)) return 'S3';
  if (eff > 6) return 'S2';
  return 'S1';
}

// ─── Chip option definitions (band → numeric upper bound) ───────────────────

type PropType = 'apartment' | 'bungalow' | 'builder' | 'outhouse';

const PROPERTY_OPTIONS: ReadonlyArray<ChipOption<PropType>> = [
  { value: 'apartment', label: 'Apartment / Flat (High Rise)' },
  { value: 'bungalow', label: 'Bungalow' },
  { value: 'builder', label: 'Builder Floor' },
  { value: 'outhouse', label: 'Outhouse' },
];

const WASHROOM_OPTIONS: ReadonlyArray<ChipOption<string>> = [
  { value: '2', label: '1 – 2' },
  { value: '3', label: '3 – 4' },
  { value: '5', label: '5 – 6' },
  { value: '7', label: '7 – 8' },
  { value: '10', label: '10 +' },
];

const MEMBER_OPTIONS: ReadonlyArray<ChipOption<string>> = [
  { value: '2', label: '1 – 2' },
  { value: '3', label: '3' },
  { value: '4', label: '4' },
  { value: '5', label: '5' },
  { value: '6', label: '6' },
  { value: '7', label: '7' },
  { value: '9', label: '8 – 10' },
  { value: '12', label: '10 +' },
];

const STAFF_OPTIONS: ReadonlyArray<ChipOption<string>> = [
  { value: '0', label: 'None' },
  { value: '1', label: '1 – 2' },
  { value: '3', label: '3 – 4' },
  { value: '5', label: '5 – 6' },
  { value: '7', label: '6 +' },
];

const YESNO_OPTIONS: ReadonlyArray<ChipOption<YN>> = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

const CLIMATE_OPTIONS: ReadonlyArray<ChipOption<ClimateZone>> = [
  { value: 'extreme_cold', label: 'Extreme Cold (-25° to 30°C)' },
  { value: 'hills', label: 'Hills (0° to 35°C)' },
  { value: 'north', label: 'North (10° to 45°C)' },
  { value: 'warm', label: 'Warm (15° to 40°C)' },
  { value: 'hot_dry', label: 'Hot & Dry (30°C+)' },
];

const USAGE_OPTIONS: ReadonlyArray<ChipOption<Usage>> = [
  { value: 'low', label: 'Low — taps / bucket' },
  { value: 'medium', label: 'Medium — shower' },
  { value: 'high', label: 'High — body jets / large shower heads' },
];

const JACUZZI_OPTIONS: ReadonlyArray<ChipOption<string>> = [
  { value: '0', label: 'None' },
  { value: '1', label: '1' },
  { value: '2', label: '2' },
  { value: '3', label: '3' },
  { value: '4', label: '4 +' },
];

const SQFT_PER_SQM = 10.7639;

// ─── Page ───────────────────────────────────────────────────────────────────

export function DomesticHeatPumpCalculatorPage() {
  const navigate = useNavigate();
  const [session, setSession] = useState(() => readStoredSession());
  // C4 — Mode-A ("pick-a-model") toggle.
  const [calcMode, setCalcMode] = useState<'calculator' | 'preselected'>('calculator');

  // Form state — every step starts unset (null) so the "fill all 10" gate is
  // observable. Area defaults to '' for the same reason.
  const [propType, setPropType] = useState<PropType | null>(null);
  const [areaValue, setAreaValue] = useState<number | ''>('');
  const [unit, setUnit] = useState<'sqft' | 'sqm'>('sqft');
  const [washrooms, setWashrooms] = useState<string | null>(null);
  const [members, setMembers] = useState<string | null>(null);
  const [staff, setStaff] = useState<string | null>(null);
  const [reverseLine, setReverseLine] = useState<YN | null>(null);
  const [climate, setClimate] = useState<ClimateZone | null>(null);
  const [usage, setUsage] = useState<Usage | null>(null);
  const [jacuzzi, setJacuzzi] = useState<string | null>(null);
  const [kitchen, setKitchen] = useState<YN | null>(null);

  // Result state
  const [result, setResult] = useState<DomesticHpResult | null>(null);
  const [hw, setHw] = useState<HwBreakdown | null>(null);
  const [bestId, setBestId] = useState<DhpModel['id'] | null>(null);
  const [seriesKey, setSeriesKey] = useState<'S1' | 'S2' | 'S3' | null>(null);
  const [m2Eligible, setM2Eligible] = useState(false);
  const [m6Eligible, setM6Eligible] = useState(false);
  const [needsSeriesState, setNeedsSeriesState] = useState(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Lead capture
  const [leadName, setLeadName] = useState('');
  const [leadPhone, setLeadPhone] = useState('');
  const [leadEmail, setLeadEmail] = useState('');
  const [leadCity, setLeadCity] = useState('');
  const [leadNotes, setLeadNotes] = useState('');
  const [savedQuotationId, setSavedQuotationId] = useState<string | null>(null);
  const [savingQuote, setSavingQuote] = useState(false);
  const [leadSent, setLeadSent] = useState(false);
  const [sendingLead, setSendingLead] = useState(false);

  // Conversion hint for the area input
  const conversionHint = useMemo(() => {
    const v = typeof areaValue === 'number' ? areaValue : 0;
    if (!v) return '';
    return unit === 'sqft'
      ? `≈ ${Math.round(v * 0.0929)} sq m`
      : `≈ ${Math.round(v * SQFT_PER_SQM)} sq ft`;
  }, [areaValue, unit]);

  // All-fields-filled gate for the Calculate button
  const allFilled = useMemo(() => {
    return Boolean(
      propType &&
        typeof areaValue === 'number' &&
        areaValue > 0 &&
        washrooms &&
        members &&
        staff &&
        reverseLine &&
        climate &&
        usage &&
        jacuzzi &&
        kitchen,
    );
  }, [
    propType,
    areaValue,
    washrooms,
    members,
    staff,
    reverseLine,
    climate,
    usage,
    jacuzzi,
    kitchen,
  ]);

  if (!session) {
    return (
      <div className="mx-auto max-w-2xl p-6 mt-10 text-center">
        <AlertTriangle size={32} className="mx-auto mb-3 text-amber-600" />
        <h2 className="text-lg font-semibold text-slate-900">No active meeting session</h2>
        <p className="mt-1 text-slate-600">
          The Domestic Heat Pump calculator is only available inside a Meeting
          Mode session. Start one to bind the sizing to a lead.
        </p>
        <button
          type="button"
          onClick={() => navigate('/sales/meeting/start')}
          className="mt-4 rounded-md bg-slate-900 px-5 py-2 text-white hover:bg-slate-800"
        >
          Initiate Meeting Mode
        </button>
      </div>
    );
  }

  // Narrow session for closures
  const sess = session;

  // Switching the unit must reconvert the input value so the rep keeps the
  // same physical area (verbatim from the reference's `switchUnit`).
  function handleUnitChange(newUnit: 'sqft' | 'sqm') {
    if (newUnit === unit) return;
    const current = typeof areaValue === 'number' ? areaValue : 0;
    let converted: number | '' = '';
    if (current > 0) {
      converted =
        newUnit === 'sqm'
          ? Math.round(current * 0.0929)
          : Math.round(current * SQFT_PER_SQM);
    }
    setUnit(newUnit);
    setAreaValue(converted);
  }

  // bd AI-w4b — session refresh on mid-meeting 403
  async function refreshSession() {
    const fresh = await domesticHpApi.startSession(sess.leadId);
    const updated = {
      token: fresh.token,
      sessionId: fresh.sessionId,
      expiresAt: fresh.expiresAt,
      leadId: sess.leadId,
      leadLabel: sess.leadLabel,
    };
    writeStoredSession(updated);
    setSession(updated);
    return updated;
  }

  function buildBackendInput(): DomesticHpInput {
    if (
      !propType ||
      typeof areaValue !== 'number' ||
      areaValue <= 0 ||
      !washrooms ||
      !members ||
      !staff ||
      !reverseLine ||
      !climate ||
      !usage ||
      !jacuzzi ||
      !kitchen
    ) {
      throw new RangeError('Please complete all selections before calculating.');
    }
    // washrooms band upper-bound numeric maps to backend enum 2|3|5|7|10.
    const washroomsN = Number(washrooms) as 2 | 3 | 5 | 7 | 10;
    // staff band upper-bound numeric maps to backend enum 0|1|3|5|7.
    const staffN = Number(staff) as 0 | 1 | 3 | 5 | 7;
    return {
      property_type: propType,
      covered_area: areaValue,
      area_unit: unit,
      washrooms: washroomsN,
      family_members: Number(members),
      staff_on_hp: staffN,
      reverse_line: reverseLine === 'yes',
      climate_zone: climate,
      usage_intensity: usage,
      jacuzzi: Number(jacuzzi),
      bath_tubs: 0, // folded into jacuzzi at the service layer; reference has no separate bath_tubs chip
      kitchen_connected: kitchen === 'yes',
    };
  }

  async function handleCalculate() {
    setError(null);
    setLoading(true);
    setSavedQuotationId(null);
    setLeadSent(false);
    try {
      const input = buildBackendInput();

      // Reference-engine echo: compute hw + best/series locally so the result
      // panel renders the breakdown bars + KPI strip without waiting on the
      // backend to ship those fields.
      const sqft =
        input.area_unit === 'sqm' ? input.covered_area * SQFT_PER_SQM : input.covered_area;
      const rev: YN = input.reverse_line ? 'yes' : 'no';
      const kitchenYN: YN = input.kitchen_connected ? 'yes' : 'no';
      const hwLocal = estimateHW(
        input.climate_zone,
        input.usage_intensity,
        input.family_members,
        input.staff_on_hp,
        rev,
        sqft,
        kitchenYN,
        input.jacuzzi,
      );
      const scored = MODELS.map((m) => ({
        m,
        flags: checkModel(
          m,
          sqft,
          input.family_members,
          input.washrooms,
          kitchenYN,
          rev,
          input.jacuzzi,
          input.staff_on_hp,
          input.usage_intensity,
          hwLocal.total,
        ),
      }));
      const primaryPass = scored
        .filter((r) => r.m.type === 'hotcold' && r.flags.length === 0)
        .sort((a, b) => a.m.kw - b.m.kw || a.m.tank - b.m.tank);
      const best = primaryPass[0]?.m ?? null;
      const m2res = scored.find((r) => r.m.id === 'M2');
      const m6res = scored.find((r) => r.m.id === 'M6');
      const m2Eok =
        Boolean(m2res && m2res.flags.length === 0 && best && best.id !== 'M2');
      const m6Eok = Boolean(m6res && m6res.flags.length === 0);
      const effMembers = input.family_members + input.staff_on_hp * 0.5;
      const needsSeries =
        input.usage_intensity === 'high' ||
        (input.jacuzzi > 0 && input.family_members > 4) ||
        sqft > 7500 ||
        effMembers > 7 ||
        !best;
      const sKey = needsSeries
        ? pickSeriesKey(sqft, input.family_members, input.staff_on_hp, input.jacuzzi)
        : null;

      setHw(hwLocal);
      setBestId(best?.id ?? null);
      setSeriesKey(sKey);
      setM2Eligible(m2Eok);
      setM6Eligible(m6Eok);
      setNeedsSeriesState(needsSeries);

      // Backend round-trip — service.ts overlay returns BOM + annual kWh +
      // engineering notes that the local echo doesn't compute.
      let res: DomesticHpResult;
      try {
        res = (await domesticHpApi.calculate(input, sess.token)) as DomesticHpResult;
      } catch (e: any) {
        if (e?.response?.status === 403) {
          const fresh = await refreshSession();
          res = (await domesticHpApi.calculate(input, fresh.token)) as DomesticHpResult;
        } else {
          throw e;
        }
      }
      setResult(res);

      // Scroll into view
      window.setTimeout(() => {
        document
          .getElementById('dhp-results')
          ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    } catch (e: any) {
      setError(
        e?.response?.data?.details?.message ||
          e?.response?.data?.error ||
          e?.message ||
          'Calculation failed. Please review the inputs.',
      );
    } finally {
      setLoading(false);
    }
  }

  async function handleSaveQuotation(): Promise<string | null> {
    if (!result) return null;
    setSavingQuote(true);
    setError(null);
    try {
      const input = buildBackendInput();
      const out = await domesticHpApi.saveQuotation(input, result, sess.token);
      setSavedQuotationId(out.id);
      return out.id;
    } catch (e: any) {
      setError(
        e?.response?.data?.details?.message ||
          e?.response?.data?.error ||
          e?.message ||
          'Could not save quotation.',
      );
      return null;
    } finally {
      setSavingQuote(false);
    }
  }

  async function handleSendLead() {
    if (!leadName.trim() || !leadPhone.trim()) {
      setError(
        'Please enter your name and mobile number so the Sunstellar team can reach you.',
      );
      return;
    }
    setSendingLead(true);
    setError(null);
    try {
      if (!savedQuotationId && result) {
        await handleSaveQuotation();
      }
      setLeadSent(true);
    } catch (e: any) {
      setError(
        e?.response?.data?.details?.message ||
          e?.response?.data?.error ||
          e?.message ||
          'Could not send enquiry. Please retry.',
      );
    } finally {
      setSendingLead(false);
    }
  }

  function handlePrint() {
    if (!result) {
      setError('Please calculate a recommendation first.');
      return;
    }
    window.print();
  }

  const best = bestId ? MODELS.find((m) => m.id === bestId) ?? null : null;
  const seriesCfg = seriesKey ? SERIES[seriesKey] : null;
  const m2 = MODELS[1];
  const m6 = MODELS[5];

  return (
    <div className="min-h-screen bg-stone-50 px-4 py-6 print:bg-white print:p-0">
      <div className="mx-auto max-w-3xl">
        {/* Top utility bar — back-link to the chooser */}
        <div className="mb-3 flex items-center gap-2 print:hidden">
          <button
            type="button"
            onClick={() => navigate('/sales/meeting/calculators')}
            className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"
          >
            <ArrowLeft size={14} /> Choose another calculator
          </button>
        </div>

        {/* Header */}
        <div className="mb-4 rounded-lg bg-white px-6 py-5 shadow-sm">
          <h1 className="text-lg font-semibold text-slate-900">
            Heat Pump — Water Heating Selector
          </h1>
          <p className="mt-1 text-xs text-slate-500">
            Estimates your daily hot water requirement, then recommends the right model
            {session.leadLabel ? ` · ${session.leadLabel}` : ''}
          </p>
        </div>

        {/* Form — 10 numbered steps */}
        {/* C4 — Mode-A toggle: Run calculator | Pick a model */}
        <div className="mb-4 inline-flex overflow-hidden rounded-lg border border-slate-300 text-sm print:hidden">
          <button type="button" onClick={() => setCalcMode('calculator')}
            className={`px-4 py-2 ${calcMode === 'calculator' ? 'bg-[#0b3d2e] text-white' : 'bg-white text-slate-700'}`}>Run calculator</button>
          <button type="button" onClick={() => setCalcMode('preselected')}
            className={`px-4 py-2 ${calcMode === 'preselected' ? 'bg-[#0b3d2e] text-white' : 'bg-white text-slate-700'}`}>Pick a model</button>
        </div>
        {calcMode === 'preselected' && (
          <div className="mb-4 rounded-lg bg-white p-6 shadow-sm print:hidden">
            <PickAModelPanel calc="domestic_heat_pump" leadId={session.leadId ?? undefined} />
          </div>
        )}
        <div className={`space-y-2.5 print:hidden${calcMode === 'preselected' ? ' hidden' : ''}`} data-testid="dhp-form">
          {/* Q1 */}
          <NumberedStep n={1} label="Property type">
            <ChipPicker<PropType>
              legend=""
              options={PROPERTY_OPTIONS}
              value={propType}
              onChange={setPropType}
            />
          </NumberedStep>

          {/* Q2 */}
          <NumberedStep n={2} label="Covered area">
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="number"
                min={100}
                max={50000}
                step={unit === 'sqm' ? 5 : 50}
                value={areaValue}
                onChange={(e) =>
                  setAreaValue(e.target.value === '' ? '' : Number(e.target.value))
                }
                placeholder="e.g., 1500"
                className="w-36 rounded-lg border border-slate-300 px-3 py-1.5 text-sm focus:border-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-200"
                data-testid="dhp-area"
              />
              <div className="inline-flex overflow-hidden rounded-lg border border-slate-300 text-xs">
                {(['sqft', 'sqm'] as const).map((u) => (
                  <button
                    key={u}
                    type="button"
                    onClick={() => handleUnitChange(u)}
                    className={`px-3 py-1.5 ${
                      unit === u
                        ? 'bg-blue-50 text-blue-700'
                        : 'bg-white text-slate-600 hover:bg-slate-50'
                    }`}
                    aria-pressed={unit === u}
                    data-testid={`dhp-unit-${u}`}
                  >
                    {u === 'sqft' ? 'sq ft' : 'sq m'}
                  </button>
                ))}
              </div>
              {conversionHint && (
                <span className="text-xs text-slate-400">{conversionHint}</span>
              )}
            </div>
          </NumberedStep>

          {/* Q3 */}
          <NumberedStep n={3} label="Number of washrooms">
            <ChipPicker<string>
              legend=""
              options={WASHROOM_OPTIONS}
              value={washrooms}
              onChange={setWashrooms}
            />
          </NumberedStep>

          {/* Q4 */}
          <NumberedStep n={4} label="Number of family members">
            <ChipPicker<string>
              legend=""
              options={MEMBER_OPTIONS}
              value={members}
              onChange={setMembers}
            />
          </NumberedStep>

          {/* Q5 */}
          <NumberedStep n={5} label="Staff connected to heat pump">
            <ChipPicker<string>
              legend=""
              options={STAFF_OPTIONS}
              value={staff}
              onChange={setStaff}
            />
          </NumberedStep>

          {/* Q6 */}
          <NumberedStep n={6} label="Reverse line & circulation pump installed?">
            <ChipPicker<YN>
              legend=""
              options={YESNO_OPTIONS}
              value={reverseLine}
              onChange={setReverseLine}
              variant="yes-no"
            />
            <div className="mt-3 rounded-lg bg-slate-50 px-3 py-2.5">
              <p className="text-xs leading-relaxed text-slate-600">
                A <strong className="font-medium text-slate-900">reverse line</strong>{' '}
                is a hot water circulation loop — hot water travels from the heat pump
                through supply pipes to all taps, and a return pipe brings unused water
                back. A circulation pump keeps hot water moving continuously so every
                tap delivers{' '}
                <strong className="font-medium text-slate-900">instant hot water</strong>{' '}
                the moment it is opened.
              </p>
            </div>
          </NumberedStep>

          {/* Q7 */}
          <NumberedStep n={7} label="Area / climate zone">
            <ChipPicker<ClimateZone>
              legend=""
              options={CLIMATE_OPTIONS}
              value={climate}
              onChange={setClimate}
            />
          </NumberedStep>

          {/* Q8 */}
          <NumberedStep n={8} label="Daily hot water usage per person">
            <ChipPicker<Usage>
              legend=""
              options={USAGE_OPTIONS}
              value={usage}
              onChange={setUsage}
            />
          </NumberedStep>

          {/* Q9 */}
          <NumberedStep n={9} label="Jacuzzi / bath tubs">
            <ChipPicker<string>
              legend=""
              options={JACUZZI_OPTIONS}
              value={jacuzzi}
              onChange={setJacuzzi}
            />
          </NumberedStep>

          {/* Q10 */}
          <NumberedStep n={10} label="Kitchen connected to heat pump?">
            <ChipPicker<YN>
              legend=""
              options={YESNO_OPTIONS}
              value={kitchen}
              onChange={setKitchen}
              variant="yes-no"
            />
          </NumberedStep>

          {error && (
            <div
              role="alert"
              className="mt-2 rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-sm text-rose-800"
              data-testid="dhp-error"
            >
              {error}
            </div>
          )}

          <button
            type="button"
            onClick={handleCalculate}
            disabled={!allFilled || loading}
            className="mt-2 w-full rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-400"
            data-testid="dhp-calculate"
          >
            {loading ? 'Calculating…' : 'Calculate requirement & get model →'}
          </button>
        </div>

        {/* Results */}
        {result && hw && (
          <div id="dhp-results" className="mt-6" data-testid="dhp-results">
            {/* Breakdown bars */}
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              Daily hot water requirement
            </p>
            <BreakdownPanel
              hw={hw}
              members={Number(members)}
              staff={Number(staff)}
              jacuzzi={Number(jacuzzi)}
              kitchen={kitchen as YN}
              reverseLine={reverseLine as YN}
              usage={usage as Usage}
            />

            {/* KPI strip */}
            <p className="mb-2 mt-4 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              Capacity summary
            </p>
            <KpiStrip
              tiles={[
                { label: 'Daily requirement', value: hw.total, unit: 'L / day' },
                { label: 'Base per person', value: hw.base, unit: 'L / day' },
                {
                  label: 'Effective occupancy',
                  value: (Number(members) + Number(staff) * 0.5).toFixed(1),
                  unit: 'persons',
                },
                { label: 'Area pipe-loss adder', value: hw.pct, unit: '%' },
              ]}
            />

            {/* Recommendation cards */}
            <p className="mb-2 mt-4 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
              Recommended heat pump system
            </p>
            <div className="space-y-2.5">
              {!best ? (
                <div
                  className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
                  data-testid="dhp-no-best-warn"
                >
                  No single model fully meets all requirements. A{' '}
                  <strong>series installation</strong> is recommended.
                </div>
              ) : (
                <ModelCard
                  m={best}
                  totalL={hw.total}
                  variant="best"
                  topLabel="Recommended model"
                />
              )}

              {needsSeriesState && seriesCfg && (
                <SeriesCard
                  cfg={seriesCfg}
                  keyLabel={seriesKey ?? ''}
                  rev={reverseLine as YN}
                  kitchen={kitchen as YN}
                  effMembers={Number(members) + Number(staff) * 0.5}
                  totalL={hw.total}
                />
              )}

              {m2Eligible && (
                <>
                  <OrDivider text="also available — smaller tank option" />
                  <ModelCard
                    m={m2}
                    totalL={hw.total}
                    variant="alt"
                    topLabel="Alternative — 300 L standard"
                  />
                </>
              )}

              {m6Eligible && (
                <>
                  <OrDivider text="or — budget / heating only option" />
                  <ModelCard
                    m={m6}
                    totalL={hw.total}
                    variant="budget"
                    topLabel="Budget option — heating only"
                  />
                </>
              )}
            </div>

            <p className="mt-4 text-[11px] leading-relaxed text-slate-400">
              Hot water estimation: North &amp; Hills — Low 30–40 L, Medium 50–60 L,
              High 80–100 L per person/day. Warm &amp; Hot &amp; Dry — Low 20–30 L,
              Medium 40–50 L, High 60–70 L. Midpoints used. Staff at 30 L/day. Kitchen
              100 L/day. Jacuzzi 150 L/tub. Staff counted at 0.5× for series selection.
              Final configuration must be confirmed by a certified HVAC engineer with an
              on-site survey.
            </p>

            {/* Backend BOM / annual kWh (AURA-only addition) — hidden in print
                summary block to keep customer-facing rendition close to ref */}
            {result.bom && result.bom.length > 0 && (
              <div className="mt-5 rounded-lg border border-slate-200 bg-white p-4 print:hidden">
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Bill of materials
                </div>
                <div className="mt-2 space-y-1 text-sm">
                  {result.bom.map((b, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between border-b border-slate-100 py-1 last:border-none"
                    >
                      <span className="text-slate-700">
                        {b.sku} × {b.qty}
                      </span>
                      <span className="font-mono text-slate-900">
                        ₹{b.capex_inr.toLocaleString('en-IN')}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                  <span>Estimated annual kWh</span>
                  <span className="font-mono text-slate-700">
                    {Math.round(result.annual_kwh).toLocaleString('en-IN')} kWh
                  </span>
                </div>
              </div>
            )}

            {/* Lead form — visual parity with reference */}
            <div
              className="mt-6 rounded-lg bg-white p-5 shadow-sm print:hidden"
              data-testid="dhp-lead"
            >
              <h3 className="text-sm font-semibold text-slate-900">
                Get a detailed quote
              </h3>
              <p className="mt-1 text-xs text-slate-600">
                Share your details and our team will send you a customised quote based
                on this recommendation.
              </p>

              <div className="mt-3 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                <LeadField
                  label="Full name *"
                  value={leadName}
                  onChange={setLeadName}
                  placeholder="Your name"
                  testId="dhp-lead-name"
                />
                <LeadField
                  label="Mobile number *"
                  value={leadPhone}
                  onChange={setLeadPhone}
                  placeholder="+91 98XXXXXXXX"
                  type="tel"
                  testId="dhp-lead-phone"
                />
                <LeadField
                  label="Email address"
                  value={leadEmail}
                  onChange={setLeadEmail}
                  placeholder="you@example.com"
                  type="email"
                />
                <LeadField
                  label="City"
                  value={leadCity}
                  onChange={setLeadCity}
                  placeholder="Delhi, Mumbai…"
                />
              </div>

              <div className="mt-2.5">
                <LeadField
                  label="Any specific requirement or question?"
                  value={leadNotes}
                  onChange={setLeadNotes}
                  placeholder="Optional notes…"
                />
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={handleSendLead}
                  disabled={sendingLead || leadSent}
                  className="inline-flex flex-1 min-w-[180px] items-center justify-center gap-2 rounded-lg bg-slate-900 px-4 py-2.5 text-sm font-semibold text-white hover:bg-slate-700 disabled:cursor-not-allowed disabled:bg-slate-400"
                  data-testid="dhp-send-lead"
                >
                  <Send size={14} />
                  {leadSent
                    ? 'Enquiry sent'
                    : sendingLead
                      ? 'Sending…'
                      : 'Send enquiry →'}
                </button>
                <button
                  type="button"
                  onClick={handlePrint}
                  className="inline-flex flex-1 min-w-[180px] items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50"
                  data-testid="dhp-print"
                >
                  <Printer size={14} />
                  Print / Save as PDF
                </button>
              </div>

              {leadSent && (
                <div
                  className="mt-3 rounded-md border-l-4 border-emerald-600 bg-emerald-50 px-4 py-2 text-sm text-emerald-800"
                  data-testid="dhp-lead-confirm"
                >
                  ✓ Enquiry sent! Our team will contact you within 24 hours.
                </div>
              )}
            </div>

            {/* AURA — save & convert (hidden in print, not in widget reference) */}
            <div
              className="mt-5 rounded-lg bg-white p-5 shadow-sm print:hidden"
              data-testid="dhp-aura-block"
            >
              <div className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                AURA — save &amp; convert
              </div>
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={handleSaveQuotation}
                  disabled={savingQuote || !!savedQuotationId}
                  className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                  data-testid="dhp-save-quote"
                >
                  {savedQuotationId
                    ? 'Quotation saved'
                    : savingQuote
                      ? 'Saving…'
                      : 'Save Quotation'}
                </button>
                {savedQuotationId && (
                  <ConvertToProposalButton
                    calculatorKey="domestic_heat_pump"
                    quotationId={savedQuotationId}
                    customerName={session.leadLabel ?? undefined}
                    leadId={session.leadId ?? undefined}
                  />
                )}
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 text-center text-xs text-slate-400 print:hidden">
          Sunstellar Heat Pump Selector · v2.0 · Bapauli, Haryana · Noida · Delhi
        </div>
      </div>
      {/* Keep proposalsApi import live for parity with other calc pages. */}
      <span className="sr-only">{typeof proposalsApi}</span>
    </div>
  );
}

// ─── Subcomponents ──────────────────────────────────────────────────────────

function NumberedStep({
  n,
  label,
  children,
}: {
  n: number;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3.5 shadow-sm">
      <div className="mb-2.5 flex items-center gap-2.5">
        <span
          className="flex h-5.5 w-5.5 shrink-0 items-center justify-center rounded-full bg-blue-50 text-[11px] font-semibold text-blue-600"
          style={{ width: 22, height: 22 }}
          aria-hidden
        >
          {n}
        </span>
        <span className="text-sm font-medium text-slate-900">{label}</span>
      </div>
      <div>{children}</div>
    </div>
  );
}

function BreakdownPanel({
  hw,
  members,
  staff,
  jacuzzi,
  kitchen,
  reverseLine,
  usage,
}: {
  hw: HwBreakdown;
  members: number;
  staff: number;
  jacuzzi: number;
  kitchen: YN;
  reverseLine: YN;
  usage: Usage;
}) {
  const climGroupLabel = hw.cg === 'north_hills' ? 'North & Hills' : 'Warm & Hot & Dry';
  const usageTxt = { low: 'Low', medium: 'Medium', high: 'High' }[usage];
  const rows = [
    {
      label: `Family — ${members} × ${hw.base} L × area factor ${(1 + hw.pct / 100).toFixed(2)}`,
      val: hw.familyL,
    },
    { label: `Staff — ${staff} × 30 L/day (low consumption)`, val: hw.staffL },
    { label: 'Kitchen — fixed 100 L/day', val: hw.kitchenL },
    { label: `Jacuzzi / bath tub — ${jacuzzi} × 150 L`, val: hw.jacuzziL },
  ].filter((r) => r.val > 0);
  const maxVal = Math.max(...rows.map((r) => r.val), 1);

  return (
    <div className="rounded-xl border border-slate-200 bg-white px-5 py-4">
      <div className="mb-3 text-xs font-medium text-slate-900">
        Breakdown — {climGroupLabel} · {usageTxt} usage · {hw.pct}% pipe-loss adder
        {hw.pct > 0 && (
          <>
            {' '}
            ({reverseLine === 'yes' ? 'reverse line installed' : 'no reverse line'})
          </>
        )}
      </div>
      {rows.map((r, i) => {
        const pct = Math.round((r.val / hw.total) * 100);
        const barPct = Math.round((r.val / maxVal) * 100);
        return (
          <div
            key={i}
            className="flex items-center gap-2 border-b border-slate-100 py-1.5 last:border-none"
          >
            <span className="flex-1 text-xs text-slate-600">{r.label}</span>
            <div className="h-1.5 w-32 overflow-hidden rounded-full bg-slate-100">
              <div
                className="h-full rounded-full bg-emerald-500"
                style={{ width: `${barPct}%` }}
              />
            </div>
            <span className="min-w-[70px] text-right text-xs font-medium text-slate-900">
              {r.val} L{' '}
              <span className="text-[11px] font-normal text-slate-400">{pct}%</span>
            </span>
          </div>
        );
      })}
      <div className="mt-1 flex items-center justify-between border-t border-slate-300 pt-2.5">
        <span className="text-xs font-semibold text-slate-900">
          Total daily hot water requirement
        </span>
        <span className="flex items-baseline gap-1">
          <span className="text-2xl font-bold text-blue-600">{hw.total}</span>
          <span className="text-xs text-slate-400">L / day</span>
        </span>
      </div>
      {hw.pct > 0 && (
        <p className="mt-2 text-[11px] leading-relaxed text-slate-400">
          Area pipe-loss adder of {hw.pct}% applied — larger properties have longer pipe
          runs so more water cools before reaching the tap.
        </p>
      )}
    </div>
  );
}

function ModelCard({
  m,
  totalL,
  variant,
  topLabel,
}: {
  m: DhpModel;
  totalL: number;
  variant: 'best' | 'alt' | 'budget';
  topLabel: string;
}) {
  const cardBorder =
    variant === 'best'
      ? 'border-2 border-blue-600'
      : variant === 'budget'
        ? 'border-2 border-amber-500'
        : 'border border-slate-200';
  const topBadge =
    variant === 'best'
      ? 'bg-blue-50 text-blue-700'
      : variant === 'budget'
        ? 'bg-amber-50 text-amber-800'
        : 'bg-slate-100 text-slate-600';
  const specHi =
    variant === 'budget'
      ? 'bg-amber-50 text-amber-800'
      : 'bg-emerald-50 text-emerald-800';
  const dot =
    variant === 'best'
      ? 'bg-blue-600'
      : variant === 'budget'
        ? 'bg-amber-500'
        : 'bg-slate-400';

  const capBadge =
    m.type === 'heatonly' ? (
      <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-[11px] font-medium text-amber-800">
        Heating only
      </span>
    ) : (
      <span className="rounded-full bg-rose-50 px-2.5 py-0.5 text-[11px] font-medium text-rose-800">
        Hot &amp; cold capable
      </span>
    );
  const revBadge = m.reverseLineOK ? (
    <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-medium text-emerald-800">
      Reverse line OK
    </span>
  ) : (
    <span className="rounded-full bg-rose-50 px-2.5 py-0.5 text-[11px] font-medium text-rose-800">
      No reverse line
    </span>
  );
  const kitchenBadge =
    m.kitchenOK === true ? (
      <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-medium text-emerald-800">
        Kitchen OK
      </span>
    ) : m.kitchenOK === 'conditional' ? (
      <span className="rounded-full bg-amber-50 px-2.5 py-0.5 text-[11px] font-medium text-amber-800">
        Kitchen ≤ 4 members
      </span>
    ) : (
      <span className="rounded-full bg-rose-50 px-2.5 py-0.5 text-[11px] font-medium text-rose-800">
        No kitchen
      </span>
    );

  return (
    <div
      className={`rounded-xl bg-white px-5 py-4 ${cardBorder}`}
      data-testid={`dhp-card-${variant}`}
    >
      <div className="mb-2 flex flex-wrap gap-1.5">
        <span
          className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${topBadge}`}
        >
          {topLabel}
        </span>
        {capBadge}
        {revBadge}
        {kitchenBadge}
      </div>
      <div className="text-sm font-semibold text-slate-900">{m.name}</div>
      <div className="mb-2.5 text-xs text-slate-400">
        Model: {m.id} · {m.series} Series · Air-source heat pump
      </div>
      <div className="mb-2.5 flex flex-wrap gap-1.5">
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${specHi}`}>
          {m.kw} kW
        </span>
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${specHi}`}>
          {m.tank} L tank
        </span>
        <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${specHi}`}>
          Covers {totalL} L/day
        </span>
        <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs text-slate-600">
          Max {m.maxMembers} members
        </span>
        <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs text-slate-600">
          Max {m.maxWashrooms} washrooms
        </span>
        <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs text-slate-600">
          Up to {m.maxSqft.toLocaleString()} sq ft
        </span>
        <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs text-slate-600">
          Staff: max {m.maxStaff}
        </span>
      </div>
      <div className="grid grid-cols-1 gap-1 border-t border-slate-100 pt-2.5 sm:grid-cols-2">
        {m.features.map((f, i) => (
          <div key={i} className="flex items-start gap-1.5 text-xs text-slate-600">
            <span className={`mt-1.5 h-1 w-1 shrink-0 rounded-full ${dot}`} />
            <span className="leading-snug">{f}</span>
          </div>
        ))}
      </div>
      <p className="mt-2.5 border-t border-slate-100 pt-2 text-[11px] leading-relaxed text-slate-500">
        {m.notes}
      </p>
    </div>
  );
}

function SeriesCard({
  cfg,
  keyLabel,
  rev,
  kitchen,
  effMembers,
  totalL,
}: {
  cfg: SeriesConfig;
  keyLabel: string;
  rev: YN;
  kitchen: YN;
  effMembers: number;
  totalL: number;
}) {
  const rlNote =
    rev === 'yes'
      ? 'Cold water inlet and reverse line (circulation return) connect to HP1 only.'
      : 'No reverse line. Cold water inlet connects to HP1 only.';

  return (
    <div
      className="rounded-xl border-2 border-emerald-500 bg-white px-5 py-4"
      data-testid="dhp-card-series"
    >
      <div className="mb-2 flex flex-wrap gap-1.5">
        <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-medium text-emerald-800">
          Series installation — {keyLabel}
        </span>
        <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-[11px] font-medium text-emerald-800">
          Uninterrupted hot water
        </span>
      </div>
      <div className="text-sm font-semibold text-slate-900">{cfg.label}</div>
      <div className="mb-2.5 text-xs text-slate-400">When to use: {cfg.when}</div>

      <div className="my-3 flex flex-wrap items-stretch gap-2">
        <div className="flex-1 min-w-[140px] rounded-lg bg-slate-50 px-3 py-2.5">
          <div className="text-[11px] leading-snug text-slate-400">
            HP1 — Primary
            <br />
            Cold water in
            {rev === 'yes' ? ' + circulation return' : ''}
          </div>
          <div className="mt-1 text-sm font-semibold text-slate-900">
            {cfg.m1.kw} kW · {cfg.m1.tank} L
          </div>
          <div className="mt-0.5 text-[11px] text-slate-400">{cfg.m1.name}</div>
        </div>
        <div className="flex shrink-0 flex-col items-center justify-center gap-0.5">
          <div className="text-xl text-slate-400">→</div>
          <div className="max-w-[64px] text-center text-[10px] leading-tight text-slate-400">
            Pre-heated water feeds HP2
          </div>
        </div>
        <div className="flex-1 min-w-[140px] rounded-lg bg-slate-50 px-3 py-2.5">
          <div className="text-[11px] leading-snug text-slate-400">
            HP2 — Secondary
            <br />
            Pre-heated water in (no cold mixing)
          </div>
          <div className="mt-1 text-sm font-semibold text-slate-900">
            {cfg.m2.kw} kW · {cfg.m2.tank} L
          </div>
          <div className="mt-0.5 text-[11px] text-slate-400">{cfg.m2.name}</div>
        </div>
      </div>

      <div className="mt-2 flex flex-wrap gap-1.5">
        <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
          Total: {cfg.totalKW} kW
        </span>
        <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
          Storage: {cfg.totalTank} L
        </span>
        <span className="rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-800">
          Covers {totalL} L/day
        </span>
        <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs text-slate-600">
          Eff. occupancy: {effMembers.toFixed(1)}
        </span>
        <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs text-slate-600">
          {kitchen === 'yes' ? 'Kitchen on HP1' : 'No kitchen'}
        </span>
        <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-0.5 text-xs text-slate-600">
          {rev === 'yes' ? 'Circulation on HP1' : 'No reverse line'}
        </span>
      </div>

      <p className="mt-3 border-t border-slate-100 pt-2 text-[11px] leading-relaxed text-slate-500">
        <strong className="font-medium text-slate-700">How it works:</strong> {rlNote}{' '}
        Hot water from HP1 feeds into HP2&apos;s inlet — no cold mixing at HP2. HP2
        raises pre-heated water to final delivery temperature, ensuring continuous
        supply during peak usage.
      </p>
    </div>
  );
}

function OrDivider({ text }: { text: string }) {
  return (
    <div className="flex items-center gap-2.5 py-1">
      <div className="flex-1 border-t border-slate-200" />
      <span className="whitespace-nowrap text-[11px] text-slate-400">{text}</span>
      <div className="flex-1 border-t border-slate-200" />
    </div>
  );
}

function LeadField({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  testId,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: string;
  testId?: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-xs font-medium text-slate-600">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
        data-testid={testId}
      />
    </div>
  );
}
